import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  Input,
  Spinner,
} from "reactstrap";
import "./style.css";

const buildSelectionSets = (selectedIdsByType = {}) => {
  const map = new Map();
  for (const [type, ids] of Object.entries(selectedIdsByType)) {
    map.set(type, new Set(ids || []));
  }
  return map;
};

const collectSelectableTypes = (nodes = [], accumulator = new Set()) => {
  for (const node of nodes) {
    if (!node) return;
    if (node.selectable !== false) {
      accumulator.add(node.type);
    }
    collectSelectableTypes(node.items || [], accumulator);
  }
  return accumulator;
};

const cloneSelectionSets = (selectionSets) => {
  const cloned = new Map();
  for (const [type, set] of selectionSets) {
    cloned.set(type, new Set(set));
  }
  return cloned;
};

const isNodeSelectable = (selectionSets, node) =>
  node && selectionSets.has(node.type) && node.selectable !== false;

const isNodeSelected = (selectionSets, node) => {
  if (!isNodeSelectable(selectionSets, node)) return false;
  return selectionSets.get(node.type).has(node.id);
};

const hasSelectedDescendants = (selectionSets, node) => {
  if (!node?.items?.length) return false;
  for (const child of node.items) {
    if (isNodeSelected(selectionSets, child)) return true;
    if (hasSelectedDescendants(selectionSets, child)) return true;
  }
  return false;
};

const collectSelectableIds = (
  selectionSets,
  node,
  accumulator = new Map(),
  includeDescendants = true
) => {
  if (!node) return accumulator;
  if (isNodeSelectable(selectionSets, node)) {
    if (!accumulator.has(node.type)) accumulator.set(node.type, new Set());
    accumulator.get(node.type).add(node.id);
  }
  if (includeDescendants) {
    for (const child of node.items || []) {
      collectSelectableIds(
        selectionSets,
        child,
        accumulator,
        includeDescendants
      );
    }
  }
  return accumulator;
};

const reconcileSelectionSets = (selectionSets, nodes, cascadeSelection) => {
  if (!cascadeSelection) {
    return selectionSets;
  }
  const visit = (node) => {
    const children = node.items || [];
    const childResults = children.map(visit);

    const childAllSelected = childResults.every(
      (child) => child.allSelectableSelected
    );
    const childAnySelected = childResults.some(
      (child) => child.anySelectableSelected
    );

    const nodeSelectable = isNodeSelectable(selectionSets, node);
    const nodeSet = selectionSets.get(node.type);
    let isSelected = nodeSelectable ? nodeSet.has(node.id) : false;

    if (nodeSelectable && children.length > 0) {
      if (childAllSelected && !isSelected) {
        nodeSet.add(node.id);
        isSelected = true;
      } else if (!childAllSelected && isSelected) {
        nodeSet.delete(node.id);
        isSelected = false;
      }
    }

    const anySelectableSelected = nodeSelectable
      ? isSelected || childAnySelected
      : childAnySelected;

    const allSelectableSelected = nodeSelectable
      ? isSelected && (children.length === 0 || childAllSelected)
      : childAllSelected;

    return {
      anySelectableSelected,
      allSelectableSelected,
    };
  };

  for (const node of nodes || []) {
    visit(node);
  }
  return selectionSets;
};

const TriStateCheckbox = ({
  checked = false,
  indeterminate = false,
  onChange = () => {},
  disabled = false,
}) => {
  const ref = useCallback(
    (node) => {
      if (node) {
        node.indeterminate = indeterminate;
      }
    },
    [indeterminate]
  );

  return (
    <input
      type="checkbox"
      className="hierarchical-selector-checkbox"
      ref={ref}
      checked={checked}
      onChange={onChange}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      disabled={disabled}
    />
  );
};

TriStateCheckbox.propTypes = {
  checked: PropTypes.bool,
  indeterminate: PropTypes.bool,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
};


const HierarchicalSelector = ({
  data = [],
  selectedIdsByType = {},
  onSelectionChange,
  className = "",
  disabled = false,
  isLoading = false,
  labels = null,
  summaryFormatter = null,
  selectDescendants = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedKeys, setExpandedKeys] = useState([]);
  const mergedLabels = useMemo(() => labels || {}, [labels]);

  const selectableTypes = useMemo(
    () => collectSelectableTypes(data || []),
    [data]
  );

  const selectionSets = useMemo(() => {
    const sets = buildSelectionSets(selectedIdsByType);
    for (const type of selectableTypes) {
      if (!sets.has(type)) {
        sets.set(type, new Set());
      }
    }
    return sets;
  }, [selectedIdsByType, selectableTypes]);

  const nodeIndex = useMemo(() => {
    const map = new Map();
    const visit = (node) => {
      if (!node) return;
      map.set(`${node.type}:${node.id}`, node);
      for (const child of node.items || []) {
        visit(child);
      }
    };
    for (const node of data || []) {
      visit(node);
    }
    return map;
  }, [data]);

  const toggleDropdown = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm("");
      setExpandedKeys([]);
    }
  }, [isOpen]);

  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  const ensureExpandedForSearch = normalizedSearchTerm.length > 0;

  const prioritizeBySelection = useCallback(
    (nodes = []) => {
      const selected = [];
      const unselected = [];

      for (const node of nodes) {
        const nodeSelected =
          isNodeSelected(selectionSets, node) ||
          hasSelectedDescendants(selectionSets, node);
        if (nodeSelected) {
          selected.push(node);
        } else {
          unselected.push(node);
        }
      }

      return [...selected, ...unselected];
    },
    [selectionSets]
  );

  const filteredData = useMemo(() => {
    const filterNodes = (nodes) => {
      if (!normalizedSearchTerm) return nodes;

      return nodes
        .map((node) => {
          const labelMatches = (node.label || "")
            .toLowerCase()
            .includes(normalizedSearchTerm);
          const filteredChildren = filterNodes(node.items || []);
          if (labelMatches) {
            return { ...node, items: filteredChildren };
          }
          if (filteredChildren.length > 0) {
            return { ...node, items: filteredChildren };
          }
          return null;
        })
        .filter(Boolean);
    };

    const reorderNodes = (nodes) => {
      const processed = nodes.map((node) => ({
        ...node,
        items: reorderNodes(node.items || []),
      }));

      return prioritizeBySelection(processed);
    };

    const filtered = filterNodes(data || []);
    return reorderNodes(filtered);
  }, [data, normalizedSearchTerm, prioritizeBySelection]);

  const hasSomeSelection = useMemo(() => {
    for (const set of selectionSets.values()) {
      if (set.size > 0) {
        return true;
      }
    }
    return false;
  }, [selectionSets]);

  const summaryText = useMemo(() => {
    return summaryFormatter ? summaryFormatter(selectedIdsByType) : "";
  }, [summaryFormatter, selectedIdsByType]);

  const handleSearchTermChange = useCallback((event) => {
    setSearchTerm(event.target.value);
  }, []);

  const handleClearSelection = useCallback(() => {
    if (!onSelectionChange) return;
    const cleared = {};
    for (const type of selectableTypes) {
      cleared[type] = [];
    }
    onSelectionChange(cleared);
  }, [onSelectionChange, selectableTypes]);

  const toggleNodeSelection = useCallback(
    (node) => {
      if (!isNodeSelectable(selectionSets, node)) return;

      const workingSets = cloneSelectionSets(selectionSets);

      const key = `${node.type}:${node.id}`;
      const fullNode = nodeIndex.get(key) || node;
      const idsByType = collectSelectableIds(
        selectionSets,
        fullNode,
        new Map(),
        selectDescendants
      );

      const nodeCurrentlySelected = isNodeSelected(selectionSets, node);

      for (const [type, ids] of idsByType) {
        if (!workingSets.has(type)) {
          workingSets.set(type, new Set());
        }
        const set = workingSets.get(type);
        for (const id of ids) {
          if (nodeCurrentlySelected) {
            set.delete(id);
          } else {
            set.add(id);
          }
        }
      }

      const normalizedSets = reconcileSelectionSets(
        workingSets,
        data || [],
        selectDescendants
      );

      const nextSelectedIdsByType = {};
      for (const [type, set] of normalizedSets) {
        nextSelectedIdsByType[type] = Array.from(set);
      }
      onSelectionChange?.(nextSelectedIdsByType);
    },
    [selectionSets, data, onSelectionChange, nodeIndex, selectDescendants]
  );

  const expandedKeySet = useMemo(() => new Set(expandedKeys), [expandedKeys]);

  const toggleNodeExpanded = useCallback((nodeKey) => {
    setExpandedKeys((prev) => {
      if (prev.includes(nodeKey)) {
        return prev.filter((key) => key !== nodeKey);
      }
      return [...prev, nodeKey];
    });
  }, []);

  const renderNode = useCallback(
    (node) => {
      const nodeKey = `${node.type}:${node.id}`;
      const children = node.items || [];
      const hasChildren = children.length > 0;
      const isExpanded =
        hasChildren && (ensureExpandedForSearch || expandedKeySet.has(nodeKey));
      const selectable = isNodeSelectable(selectionSets, node);
      const selected = isNodeSelected(selectionSets, node);
      const indeterminate =
        selectable && !selected && hasSelectedDescendants(selectionSets, node);

      return (
        <div>
          <div className="hierarchical-selector-row">
            {hasChildren ? (
              <button
                type="button"
                className="hierarchical-selector-expand"
                onClick={() => toggleNodeExpanded(nodeKey)}
              >
                <i
                  className={`fas ${isExpanded ? "fa-chevron-down" : "fa-chevron-right"}`}
                ></i>
              </button>
            ) : (
              <span className="hierarchical-selector-spacer" />
            )}
            {selectable ? (
              <TriStateCheckbox
                checked={selected}
                indeterminate={indeterminate}
                onChange={() => toggleNodeSelection(node)}
              />
            ) : (
              <span className="hierarchical-selector-spacer" />
            )}
            <span>{node.label}</span>
          </div>
          {hasChildren && isExpanded ? (
            <div className="hierarchical-selector-children">
              {children.map((child) => (
                <React.Fragment key={`${child.type}:${child.id}`}>
                  {renderNode(child)}
                </React.Fragment>
              ))}
            </div>
          ) : null}
        </div>
      );
    },
    [
      ensureExpandedForSearch,
      expandedKeySet,
      selectionSets,
      toggleNodeExpanded,
      toggleNodeSelection,
    ]
  );

  let menuContent;

  if (isLoading) {
    menuContent = (
      <div className="hierarchical-selector-loading">
        <Spinner size="sm" />
      </div>
    );
  } else if (filteredData.length === 0) {
    menuContent = (
      <div className="hierarchical-selector-empty">
        {mergedLabels.noResults ?? ""}
      </div>
    );
  } else {
    menuContent = (
      <div className="hierarchical-selector-list">
        {filteredData.map((node) => (
          <React.Fragment key={`${node.type}:${node.id}`}>
            {renderNode(node)}
          </React.Fragment>
        ))}
      </div>
    );
  }

  return (
    <Dropdown
      className={`hierarchical-selector ${className || ""}`.trim()}
      isOpen={isOpen}
      toggle={toggleDropdown}
    >
      <DropdownToggle
        className="hierarchical-selector-toggle"
        disabled={disabled}
        caret
      >
        <span className="hierarchical-selector-toggle-text">{summaryText}</span>
      </DropdownToggle>
      <DropdownMenu className="hierarchical-selector-menu" end={false}>
        <div className="hierarchical-selector-content">
          <div className="hierarchical-selector-search">
            <Input
              type="text"
              value={searchTerm}
              onChange={handleSearchTermChange}
              placeholder={mergedLabels.searchPlaceholder ?? ""}
            />
            {hasSomeSelection ? (
              <button
                type="button"
                className="hierarchical-selector-clear-selection"
                onClick={(event) => {
                  event.stopPropagation();
                  handleClearSelection();
                }}
              >
                <i className="fas fa-eraser"></i>
              </button>
            ) : null}
          </div>
          {menuContent}
        </div>
      </DropdownMenu>
    </Dropdown>
  );
};

HierarchicalSelector.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      selectable: PropTypes.bool,
      items: PropTypes.array,
    })
  ),
  selectedIdsByType: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)),
  onSelectionChange: PropTypes.func,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  labels: PropTypes.shape({
    placeholder: PropTypes.string,
    searchPlaceholder: PropTypes.string,
    noResults: PropTypes.string,
  }),
  summaryFormatter: PropTypes.func,
  selectDescendants: PropTypes.bool,
};


export default HierarchicalSelector;
